import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
// Import the interactive diagram component
import InteractiveDiagram from "../../components/InteractiveDiagram";

const IoT = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Real-Time Data Processing & IoT - limkc</title>
        <meta name="description" content="Learn about Real-Time Data Processing & IoT with real-world examples, AWS IoT Core implementation strategies, and cloud-native architectural patterns." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
  
      <div className="w-full h-full overflow-auto">
        {/* Banner Image - Full Width */}
        <div className="w-full h-48 overflow-hidden" style={{ marginTop: 'var(--navbar-height)' }}>
          <img
            src="/IMAGE/IOT.png"
            alt="IoT and Real-Time Data Processing"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Content - Centered */}
        <div className="w-full flex justify-center">
          <div className="py-6 transition-all duration-300 w-full"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--text-primary)',
              maxWidth: '1200px',
              paddingLeft: '1rem',
              paddingRight: '1rem'
            }}>

            <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Real-Time Data Processing & IoT</h1>
          <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>
            The Internet of Things (IoT) enables real-time data collection, processing, and automation  
            across millions of connected devices. Cloud-based IoT platforms like AWS IoT Core, MQTT, and Edge Computing  
            allow scalable and secure data management for smart devices, industrial sensors, and remote monitoring systems.
          </p>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Why Real-Time IoT Processing Matters</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            IoT devices generate continuous data streams that require low-latency processing and high scalability.
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li>Low Latency – Real-time event processing for smart devices and industrial automation.</li>
            <li>Scalability – Cloud-native architectures scale to millions of IoT devices.</li>
            <li>Security & Compliance – Ensuring secure device authentication and encrypted communication.</li>
            <li>Edge Processing – Running AI models and analytics directly on IoT devices to reduce cloud dependency.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>High-Throughput IoT Architecture</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            A robust IoT architecture requires multiple components working together to handle millions of concurrent
            connections while maintaining low latency and data integrity. The interactive diagram below illustrates a scalable
            AWS-based architecture for high-volume IoT data processing:
          </p>
          
          {/* <div className="relative my-4"> */}
          <div className="relative my-4 max-h-[50vh] md:max-h-[720px] overflow-auto">
            {/* Full-size diagram */}
            <InteractiveDiagram 
              name="iot-architecture" 
              alt="High-Throughput IoT Data Processing Architecture" 
              // className="w-full h-auto"
              className="w-full h-full"
            />
            <p className="text-xs text-center text-gray-500 mt-1">
              High-throughput IoT processing architecture with AWS
              <br />
              <span className="text-xs text-gray-300">© 2025 limkc. All rights reserved. Reproduction requires attribution.</span>
            </p>
          </div>
          
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            This architecture provides multiple processing paths based on data priority and volume:
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><span className="text-orange-500 font-semibold">Critical path</span> - Direct Lambda processing for immediate action</li>
            <li><span className="text-blue-500 font-semibold">High-frequency path</span> - Kinesis streams for high-volume sensor data</li>
            <li><span className="text-green-500 font-semibold">Batch path</span> - Firehose for efficient historical data storage</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>AWS IoT Core: The Foundation</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            AWS IoT Core serves as the central hub for IoT device connectivity and management. It provides a secure, scalable pipeline 
            for IoT data to flow into the AWS Cloud and other endpoints.
          </p>
  
          <div className="bg-blue-50 p-4 rounded-lg mt-4">
            <h3 className="text-xl font-semibold text-blue-700">Key AWS IoT Core Components</h3>
            <div className="grid md:grid-cols-2 gap-4 mt-3">
              <div>
                <h4 className="font-semibold text-blue-800">Message Broker</h4>
                <p className="text-gray-600 text-sm mt-1">
                  The high-throughput pub/sub message broker securely transmits messages between devices and AWS IoT using MQTT, 
                  WebSockets, and HTTP 1.1 protocols. It can handle billions of devices and trillions of messages.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800">Device Gateway</h4>
                <p className="text-gray-600 text-sm mt-1">
                  Enables devices to securely and efficiently communicate with AWS IoT Core. It supports both secure WebSocket 
                  connections and MQTT over TLS, with automatic scaling to handle connection spikes.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800">Rules Engine</h4>
                <p className="text-gray-600 text-sm mt-1">
                  A sophisticated SQL-based engine that analyzes incoming messages and transforms or routes them to AWS services. 
                  Rules can trigger Lambda functions, store data in DynamoDB, or send messages to SQS queues.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-blue-800">Device Shadow</h4>
                <p className="text-gray-600 text-sm mt-1">
                  Maintains a virtual representation (shadow) of each connected device, allowing applications to interact with devices 
                  even when they're offline or intermittently connected.
                </p>
              </div>
            </div>
          </div>
  
          <h3 className="text-xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Authentication and Security</h3>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            AWS IoT Core implements multi-layered security, critical for protecting device data and preventing unauthorized access:
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><b>X.509 certificates</b> – Each device uses unique certificates for authentication</li>
            <li><b>TLS 1.2 encryption</b> – All data in transit is encrypted using industry-standard protocols</li>
            <li><b>IoT policies</b> – Fine-grained permissions control what each device can publish or subscribe to</li>
            <li><b>Cognito integration</b> – Allows secure authentication for mobile and web applications</li>
            <li><b>Device Defender</b> – Continuously audits IoT configurations and monitors for abnormal behavior</li>
          </ul>
  
          <h3 className="text-xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>MQTT Topic Design Patterns</h3>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Effective MQTT topic design is crucial for scalable IoT architectures. Well-structured topic hierarchies enable efficient 
            message routing and filtering:
          </p>
  
          <pre className="p-4 rounded-md mt-2 text-sm overflow-x-auto" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>
  {`# Device-to-cloud telemetry data
  device/{device_id}/telemetry/{sensor_type}
  
  # Cloud-to-device commands
  device/{device_id}/commands
  
  # Device state updates
  device/{device_id}/state
  
  # Group-based messaging
  group/{group_id}/broadcast
  
  # Region-specific data collection
  region/{region_id}/devices/{device_id}/data`}
          </pre>
  
          <p className="mt-3" style={{ color: 'var(--text-secondary)' }}>
            Topic wildcards enable flexible subscription patterns. The <code className="px-1 rounded" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>+</code> wildcard matches a single level, while <code className="px-1 rounded" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>#</code> matches multiple levels:
          </p>
  
          <pre className="p-4 rounded-md mt-2 text-sm overflow-x-auto" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>
  {`# Subscribe to all temperature sensors across all devices
  device/+/telemetry/temperature
  
  # Subscribe to all telemetry from a specific device
  device/device-123/telemetry/#
  
  # Monitoring all state changes in a region
  region/us-west/devices/+/state`}
          </pre>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Data Processing Patterns</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            The following AWS services work together to create powerful IoT data processing pipelines:
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><b>AWS IoT Core</b> – Secure device-to-cloud connectivity with MQTT, HTTP, and WebSockets.</li>
            <li><b>AWS IoT Greengrass</b> – Runs local computing, AI inference, and data aggregation at the edge.</li>
            <li><b>AWS Kinesis</b> – Processes high-volume IoT data streams in real time.</li>
            <li><b>AWS Lambda</b> – Event-driven processing for IoT rule-based automation.</li>
            <li><b>Amazon Timestream</b> – Purpose-built time series database for IoT telemetry storage.</li>
            <li><b>AWS IoT Analytics</b> – Specialized analytics service for IoT data with built-in data preparation.</li>
            <li><b>Amazon EventBridge</b> – Event bus that connects IoT data with application components.</li>
          </ul>
  
          <h3 className="text-xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Implementing IoT Rules</h3>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            IoT Rules provide the intelligence layer that determines what happens when device data arrives. Here's an example of a temperature 
            monitoring rule that triggers actions when threshold values are exceeded:
          </p>
  
          <pre className="p-4 rounded-md mt-2 text-sm overflow-x-auto" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>
  {`resource "aws_iot_topic_rule" "temperature_alert" {
    name = "TemperatureAlertRule"
    sql  = "SELECT *, topic(3) as device_id FROM 'device/+/telemetry/temperature' WHERE temperature > 75"
    sql_version = "2016-03-23"
  
    action {
      lambda {
        function_arn = aws_lambda_function.process_temperature.arn
      }
    }
    
    action {
      dynamodb {
        table_name = aws_dynamodb_table.temperature_alerts.name
        role_arn   = aws_iam_role.iot_dynamodb_role.arn
        hash_key_field = "device_id"
        
         hash_key_value = "$\{device_id}"
        range_key_field = "timestamp"
      
        range_key_value = "$\{timestamp()}"
        payload_field = "data"
      }
    }
    
    action {
      sns {
        target_arn = aws_sns_topic.temperature_alerts.arn
        role_arn   = aws_iam_role.iot_sns_role.arn
        message_format = "JSON"
      }
    }
  }`}
          </pre>
  
          <p className="mt-3" style={{ color: 'var(--text-secondary)' }}>
            This rule simultaneously triggers three actions when a temperature exceeds 75 degrees:
          </p>
          <ol className="list-decimal list-inside mt-1" style={{ color: 'var(--text-secondary)' }}>
            <li>Invokes a Lambda function for custom processing</li>
            <li>Records the event in DynamoDB for persistence</li>
            <li>Sends a notification via SNS for alerting</li>
          </ol>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Real World Experience</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            I built a real-time industrial monitoring system using AWS IoT Core, MQTT, and Lambda.  
            The biggest challenges included handling high-frequency sensor data, reducing cloud latency,  
            and implementing fault tolerance.  
            Solutions included MQTT topic filtering, edge AI processing, and serverless auto-scaling.
          </p>
  
          <p className="mt-3" style={{ color: 'var(--text-secondary)' }}>
            Key lessons from implementation:
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li>Filtering data at the edge reduced cloud ingestion costs by nearly 60%</li>
            <li>Implementing device shadows significantly improved application resilience during connectivity issues</li>
            <li>Using basic authentication initially created management overhead; migrating to X.509 certificates improved security and scalability</li>
            <li>Multi-region deployment with global endpoints provided geographic redundancy and reduced latency</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Common Challenges & Solutions</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><b>Data Overload</b> – Use Kafka or Kinesis for real-time event buffering.</li>
            <li><b>Latency Issues</b> – Process data at the edge using AWS IoT Greengrass.</li>
            <li><b>Device Security</b> – Implement X.509 certificates and IoT policies for authentication.</li>
            <li><b>High Costs</b> – Optimize data ingestion with rules-based filtering and batching.</li>
            <li><b>Message Ordering</b> – Use MQTT QoS 1 and implement sequence numbers for critical messages.</li>
            <li><b>Connection Management</b> – Implement exponential backoff and jitter in device connection logic.</li>
            <li><b>Device Provisioning</b> – Use fleet provisioning templates for secure, automated onboarding.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Best Practices</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li>Use MQTT and WebSockets for real-time, low-latency communication.</li>
            <li>Enable Edge AI inference to reduce cloud bandwidth costs.</li>
            <li>Leverage AWS IoT Device Defender for security monitoring.</li>
            <li>Utilize Kinesis or Kafka for large-scale IoT event streaming.</li>
            <li>Implement device certificate rotation policies for long-lived devices.</li>
            <li>Use Basic Ingest for high-volume, one-way telemetry to reduce costs.</li>
            <li>Design for intermittent connectivity with message queuing and offline operations.</li>
            <li>Implement circuit breakers to prevent cascading failures during service disruptions.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Related Topics</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><a href="/topics/cloud-management" className="text-blue-500 hover:underline">Cloud Management & Multi-Cloud Strategy</a></li>
            <li><a href="/topics/ai-cloud-automation" className="text-blue-500 hover:underline">AI-Driven Cloud Automation</a></li>
          </ul>
  
          <button 
            onClick={() => navigate("/projects")} 
            className="mt-6 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--text-secondary)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
          >
            ← Back to Topics
          </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default IoT;